import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import PlansPanel, { type PlanItem } from './PlansPanel'
import { listPlans, deletePlan as deletePlanApi } from '@/api/agentplans'
import { useDockUiStore } from '@/store/dockUiStore'
import { useConfirm } from '@/components/common/ConfirmDialog'

interface DockPlansSlotProps {
  project: string
  // Which backend this dock drives — plans of OTHER backends can't be resumed
  // here (the dock would need to switch modes), so we guide the user instead.
  backend: 'autoagent' | 'claude' | 'codex' | 'opencode'
  busy?: boolean
  // Resume a plan that belongs to THIS dock's backend.
  onResumeOwn?: (planId: string) => void
  // Pause/stop the in-flight run (claude/codex pause == cancel the active run).
  onPauseOwn?: () => void
  // Bump to force a refetch (e.g. when streaming state changes).
  refreshSignal?: unknown
  // Reports how many plans are completed so the dock can show/hide the
  // "show completed plans" toggle in its header menu.
  onCompletedCount?: (n: number) => void
}

// Cross-backend plan switcher for the Claude/Codex docks, sourced from the REST
// registry (those backends don't push a live `plans` WS event the way the
// AutoAgent dock does).
export default function DockPlansSlot({
  project, backend, busy = false, onResumeOwn, onPauseOwn, refreshSignal,
  onCompletedCount,
}: DockPlansSlotProps) {
  const [plans, setPlans] = useState<PlanItem[]>([])
  const showCompleted = useDockUiStore((s) => s.showCompletedPlans)
  const { confirm, confirmDialog } = useConfirm()

  const refresh = useCallback(async () => {
    try {
      const list = await listPlans(project)
      const mapped = list.map((p) => {
        const todos = (p.todos || []) as Array<{ status?: string }>
        const work = todos.filter((t) => t.status !== undefined)
        return {
          id: p.id, title: p.title, kind: p.kind,
          status: p.status as PlanItem['status'], backend: p.backend,
          updated_at: p.updated_at,
          n_total: work.length,
          n_done: work.filter((t) => t.status === 'completed' || t.status === 'skipped').length,
          depends_on: p.depends_on,
        }
      })
      setPlans(mapped)
      onCompletedCount?.(mapped.filter((p) => p.status === 'completed').length)
    } catch { /* registry may be empty */ }
  }, [project, onCompletedCount])

  useEffect(() => { void refresh() }, [refresh, refreshSignal])

  const handleResume = useCallback((id: string) => {
    const p = plans.find((x) => x.id === id)
    if (p && p.backend && p.backend !== backend) {
      toast(`This is a ${p.backend} plan — switch the dock to ${p.backend} mode to resume it.`)
      return
    }
    onResumeOwn?.(id)
  }, [plans, backend, onResumeOwn])

  // A failed delete must SAY so — swallowing the error made a stuck plan look
  // undeletable (click, confirm, nothing happens, row still there).
  const handleDelete = useCallback(async (id: string) => {
    try {
      await deletePlanApi(project, id)
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 409) {
        const ok = await confirm({
          title: 'Plan is still running',
          message: 'This plan is still running. Stop it and delete it anyway?',
          confirmLabel: 'Stop and delete',
        })
        if (ok) {
          try {
            await deletePlanApi(project, id, true)
          } catch {
            toast.error("Couldn't delete the plan. Please try again.")
          }
        }
      } else if (status !== 404) {
        toast.error("Couldn't delete the plan. Please try again.")
      }
    }
    void refresh()
  }, [project, refresh, confirm])

  return (
    <>
      <PlansPanel
        plans={plans}
        activePlanId={null}
        busy={busy}
        showCompleted={showCompleted}
        onResume={handleResume}
        onPause={onPauseOwn ? () => onPauseOwn() : undefined}
        onDelete={handleDelete}
      />
      {confirmDialog}
    </>
  )
}
